import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../../context/AuthContext';
import { useSalonAccess } from '../../../context/SalonAccessContext';
import { useClientMessagingWebSocket } from '../../../hooks/useClientMessagingWebSocket';
import { messagingService } from '../../../services/messaging.service';
import { theme } from '../../../theme';
import AppointmentConversationSummary from '../../client/messaging/shared/AppointmentConversationSummary';
import MessagingConversationList from '../../client/messaging/shared/MessagingConversationList';
import MessagingThreadView from '../../client/messaging/shared/MessagingThreadView';
import ClientChatSettings from './ClientChatSettings';

const ClientInboxPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { selectedSalonId, selectedSalon } = useSalonAccess();
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [assignees, setAssignees] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [sending, setSending] = useState(false);
  const currentSalonRef = useRef(selectedSalonId);
  const selectedIdRef = useRef(selectedId);
  const listRequestRef = useRef(0);
  const detailRequestRef = useRef(0);
  const actionGenerationRef = useRef(0);
  const translateRef = useRef(t);
  translateRef.current = t;
  selectedIdRef.current = selectedId;

  useEffect(() => {
    actionGenerationRef.current += 1;
    setAssignees([]);
    setAssignOpen(false);
    setMessage('');
    setSending(false);
  }, [selectedId]);

  useEffect(() => {
    currentSalonRef.current = selectedSalonId;
    listRequestRef.current += 1;
    detailRequestRef.current += 1;
    actionGenerationRef.current += 1;
    setConversations([]);
    setSelectedId(null);
    setDetail(null);
    setAssignees([]);
    setAssignOpen(false);
    setMessage('');
    setSending(false);
    setError('');
  }, [selectedSalonId]);

  const loadList = useCallback(async () => {
    if (!selectedSalonId) return;
    const salonId = selectedSalonId;
    const requestId = ++listRequestRef.current;
    try {
      const response = await messagingService.listSalonClientConversations(
        salonId,
        statusFilter === 'all' ? {} : { status: statusFilter },
      );
      if (
        requestId !== listRequestRef.current
        || Number(currentSalonRef.current) !== Number(salonId)
      ) return;
      const items = Array.isArray(response.data) ? response.data : response.data?.results || [];
      setConversations(items);
      const requestedId = Number(new URLSearchParams(window.location.search).get('conversation'));
      setSelectedId((current) => items.some((item) => Number(item.id) === Number(current))
        ? current
        : items.find((item) => Number(item.id) === requestedId)?.id || items[0]?.id || null);
    } catch (_requestError) {
      if (requestId !== listRequestRef.current) return;
      setError(translateRef.current('client_messaging.load_error', 'Impossible de charger les discussions.'));
    }
  }, [selectedSalonId, statusFilter]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => {
    if (!selectedId || !selectedSalonId) return;
    const salonId = selectedSalonId;
    const conversationId = selectedId;
    const requestId = ++detailRequestRef.current;
    setDetail((current) => (
      Number(current?.id) === Number(conversationId) ? current : null
    ));
    setError('');
    const loadDetail = async () => {
      try {
        const response = await messagingService.retrieveClientConversation(
          conversationId,
          { salon: salonId },
        );
        if (
          requestId !== detailRequestRef.current
          || Number(currentSalonRef.current) !== Number(salonId)
        ) return;
        setDetail(response.data);
        await messagingService.markClientConversationRead(
          conversationId,
          { salon: salonId },
        );
        if (requestId !== detailRequestRef.current) return;
        setConversations((items) => items.map((item) => (
          Number(item.id) === Number(conversationId)
            ? { ...item, unread_count: 0 }
            : item
        )));
      } catch (_requestError) {
        if (requestId !== detailRequestRef.current) return;
        setError(translateRef.current('client_messaging.load_error', 'Impossible de charger la discussion.'));
      }
    };
    loadDetail();
  }, [selectedId, selectedSalonId]);

  const onSocketMessage = useCallback((event) => {
    if (event.type !== 'new_message') return;
    const conversationId = event.message?.conversation_id;
    if (Number(conversationId) === Number(selectedIdRef.current)) {
      messagingService.markClientConversationRead(
        conversationId,
        { salon: currentSalonRef.current },
      ).catch(() => {});
      setConversations((items) => items.map((item) => (
        Number(item.id) === Number(conversationId)
          ? { ...item, unread_count: 0 }
          : item
      )));
    }
    setDetail((current) => {
      if (!current || Number(conversationId) !== Number(current.id)) return current;
      if (current.messages?.some((item) => Number(item.id) === Number(event.message.id))) return current;
      return { ...current, messages: [...(current.messages || []), event.message] };
    });
    loadList();
  }, [loadList]);
  useClientMessagingWebSocket({ token: user?.token, onMessage: onSocketMessage });

  const openAssignment = async () => {
    const salonId = selectedSalonId;
    const conversationId = selectedId;
    const generation = actionGenerationRef.current;
    const response = await messagingService.listClientConversationAssignees(
      conversationId,
      { salon: salonId },
    );
    if (
      generation !== actionGenerationRef.current
      || Number(currentSalonRef.current) !== Number(salonId)
      || Number(selectedIdRef.current) !== Number(conversationId)
    ) return;
    setAssignees(response.data || []);
    setAssignOpen(true);
  };
  const assign = async (userId) => {
    const salonId = selectedSalonId;
    const conversationId = selectedId;
    const generation = actionGenerationRef.current;
    const response = await messagingService.assignClientConversation(
      conversationId,
      userId,
      { salon: salonId },
    );
    if (
      generation !== actionGenerationRef.current
      || Number(currentSalonRef.current) !== Number(salonId)
      || Number(selectedIdRef.current) !== Number(conversationId)
    ) return;
    setDetail(response.data);
    setAssignOpen(false);
    loadList();
  };
  const send = async () => {
    const content = message.trim();
    if (!content || !selectedId || sending || detail?.status === 'closed') {
      return;
    }
    const salonId = selectedSalonId;
    const conversationId = selectedId;
    const generation = actionGenerationRef.current;
    setSending(true);
    setError('');
    try {
      const response = await messagingService.sendClientConversationMessage(
        conversationId,
        content,
        { salon: salonId },
      );
      if (
        generation !== actionGenerationRef.current
        || Number(currentSalonRef.current) !== Number(salonId)
        || Number(selectedIdRef.current) !== Number(conversationId)
      ) return;
      setMessage('');
      setDetail(response.data);
      loadList();
    } catch (_requestError) {
      if (
        generation === actionGenerationRef.current
        && Number(currentSalonRef.current) === Number(salonId)
        && Number(selectedIdRef.current) === Number(conversationId)
      ) {
        setError(translateRef.current(
          'client_messaging.send_error',
          'Impossible d’envoyer le message.',
        ));
      }
    } finally {
      if (
        generation === actionGenerationRef.current
        && Number(currentSalonRef.current) === Number(salonId)
        && Number(selectedIdRef.current) === Number(conversationId)
      ) {
        setSending(false);
      }
    }
  };
  const toggleStatus = async () => {
    const salonId = selectedSalonId;
    const conversationId = selectedId;
    const generation = actionGenerationRef.current;
    const method = detail.status === 'closed' ? 'reopenClientConversation' : 'closeClientConversation';
    const response = await messagingService[method](conversationId, { salon: salonId });
    if (
      generation !== actionGenerationRef.current
      || Number(currentSalonRef.current) !== Number(salonId)
      || Number(selectedIdRef.current) !== Number(conversationId)
    ) return;
    setDetail(response.data);
    loadList();
  };

  return (
    <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 1, sm: 2 } }}>
      <Box sx={{ mb: 2.25 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            color: theme.colors.secondary[950],
            letterSpacing: '-0.02em',
          }}
        >
          {t('client_messaging.salon_inbox', 'Messages clientes')}
        </Typography>
        <Typography
          variant="body2"
          sx={{ mt: 0.5, color: theme.colors.grey[600] }}
        >
          Échangez avec vos clientes et suivez leurs demandes.
        </Typography>
      </Box>

      <ClientChatSettings salon={selectedSalon} />

      {error && !detail && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <TextField
          select
          size="small"
          label={t('client_messaging.status_filter', 'Statut')}
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
          sx={{
            minWidth: 180,
            '& .MuiOutlinedInput-root': {
              borderRadius: '14px',
              backgroundColor: theme.colors.secondary[50],
              '& fieldset': {
                borderColor: theme.colors.secondary[200],
              },
              '&:hover fieldset, &.Mui-focused fieldset': {
                borderColor: theme.colors.primary[600],
              },
            },
          }}
        >
          <MenuItem value="all">
            {t('client_messaging.all_statuses', 'Toutes')}
          </MenuItem>
          <MenuItem value="open">
            {t('client_messaging.open_status', 'Ouvertes')}
          </MenuItem>
          <MenuItem value="closed">
            {t('client_messaging.closed_status', 'Fermées')}
          </MenuItem>
        </TextField>
      </Box>

      <Paper
        elevation={0}
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '350px minmax(0, 1fr)' },
          height: {
            xs: 'calc(100dvh - 250px)',
            md: 'min(720px, calc(100vh - 250px))',
          },
          minHeight: { xs: 500, md: 560 },
          overflow: 'hidden',
          borderRadius: { xs: '20px', md: '24px' },
          border: `1px solid ${theme.colors.secondary[200]}`,
          backgroundColor: theme.colors.secondary[50],
          boxShadow: '0 22px 55px rgba(67, 44, 23, 0.10)',
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            minHeight: 0,
            display: { xs: detail ? 'none' : 'block', md: 'block' },
            borderRight: {
              md: `1px solid ${theme.colors.secondary[200]}`,
            },
          }}
        >
          <MessagingConversationList
            conversations={conversations}
            selectedId={selectedId}
            query={query}
            onQueryChange={setQuery}
            onSelect={(conversation) => setSelectedId(conversation.id)}
            getIdentity={(conversation) => (
              conversation.client?.display_name || 'Cliente'
            )}
            getPreview={(conversation) => (
              conversation.last_message?.content
              || conversation.subject
              || t(
                `client_messaging.kind_${conversation.kind}`,
                conversation.kind || 'Discussion',
              )
            )}
            emptyTitle="Aucun message client"
            emptyDescription="Les nouvelles demandes de vos clientes apparaîtront ici."
          />
        </Box>

        <Box
          sx={{
            minWidth: 0,
            minHeight: 0,
            display: { xs: detail ? 'block' : 'none', md: 'block' },
          }}
        >
          <MessagingThreadView
            identity={detail?.client?.display_name}
            subtitle={
              detail?.assigned_to?.display_name
              || t('client_messaging.unassigned', 'Non assignée')
            }
            status={detail?.status}
            messages={detail?.messages || []}
            ownSenderType="salon"
            draft={message}
            onDraftChange={setMessage}
            onSend={send}
            sending={sending}
            error={detail ? error : ''}
            context={
              detail?.kind === 'existing_appointment' && detail?.appointment ? (
                <AppointmentConversationSummary
                  appointment={detail.appointment}
                />
              ) : null
            }
            onBack={() => {
              setSelectedId(null);
              setDetail(null);
              setError('');
            }}
            actions={detail ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Button
                  color="inherit"
                  size="small"
                  onClick={openAssignment}
                  sx={{
                    minWidth: 0,
                    px: { xs: 0.75, sm: 1.25 },
                    color: theme.colors.primary[950],
                    fontWeight: 700,
                    textTransform: 'none',
                  }}
                >
                  {t('client_messaging.reassign', 'Réaffecter')}
                </Button>
                <Button
                  color="inherit"
                  size="small"
                  onClick={toggleStatus}
                  sx={{
                    minWidth: 0,
                    px: { xs: 0.75, sm: 1.25 },
                    color: theme.colors.primary[950],
                    fontWeight: 700,
                    textTransform: 'none',
                  }}
                >
                  {detail.status === 'closed'
                    ? t('messaging.unarchive', 'Rouvrir')
                    : t('messaging.archive', 'Fermer')}
                </Button>
              </Box>
            ) : null}
          />
        </Box>
      </Paper>

      <Dialog open={assignOpen} onClose={() => setAssignOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 800, color: theme.colors.secondary[950] }}>
          {t('client_messaging.reassign', 'Réaffecter')}
        </DialogTitle>
        <DialogContent>
          <List>
            {assignees.map((assignee) => (
              <ListItemButton
                key={assignee.id}
                onClick={() => assign(assignee.id)}
                sx={{
                  borderRadius: '14px',
                  '&:hover': {
                    backgroundColor: theme.colors.primary[100],
                  },
                }}
              >
                <ListItemText primary={assignee.display_name} />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ClientInboxPage;
